import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSystemStatistics, getUserActivityStats, getPlanStatistics } from '../../api/statistics';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { DonutChart, VerticalBarChart } from '../../components/admin/AdminCharts';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [systemStats, setSystemStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [planStats, setPlanStats] = useState(null);

  useEffect(() => {
    const loadAllStatistics = async () => {
      setLoading(true);
      try {
        const [system, users, plans] = await Promise.all([
          getSystemStatistics(),
          getUserActivityStats(30),
          getPlanStatistics()
        ]);
        setSystemStats(system);
        setUserStats(users);
        setPlanStats(plans);
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllStatistics();
  }, [refreshToken]);

  const handleRefresh = () => {
    setRefreshToken((current) => current + 1);
  };

  const getStatusColor = (status) => {
    const colors = {
      'brouillon': '#95a5a6',
      'analyse': '#3498db',
      'validé': '#2ecc71',
      'non validé': '#e74c3c',
      'approuvé': '#27ae60',
      'non approuvé': '#c0392b'
    };
    return colors[status] || '#7f8c8d';
  };

  const getRoleColor = (roleCode) => {
    const colors = {
      'EN': '#3498db',
      'CE': '#9b59b6',
      'CP': '#e67e22',
      'DA': '#27ae60',
      'SC': '#e74c3c'
    };
    return colors[roleCode] || '#95a5a6';
  };

  const formatValidationRoleLabel = (rawRole) => {
    const value = String(rawRole || '').trim();
    if (!value) return 'Rôle non renseigné';

    const normalized = value.toUpperCase();
    const codeMap = {
      CE: "Chef d'équipe",
      CP: 'Conseiller',
      DA: 'Direction adjointe',
      EN: 'Enseignant',
      SC: 'Spécialiste contenu',
    };

    if (codeMap[normalized]) {
      return codeMap[normalized];
    }

    return value;
  };

  const statusCounts = useMemo(() => {
    return (systemStats?.plans_by_status || []).reduce((acc, status) => {
      acc[status.statut] = status.count;
      return acc;
    }, {});
  }, [systemStats]);

  const dashboardKpis = [
    {
      label: 'Utilisateurs actifs 30 j',
      value: userStats?.active_users?.length || 0,
      helper: 'activité récente détectée',
    },
    {
      label: 'Plans approuvés',
      value: statusCounts['approuvé'] || 0,
      helper: 'validation finale obtenue',
    },
    {
      label: 'Plans à corriger',
      value: (statusCounts['non validé'] || 0) + (statusCounts['non approuvé'] || 0),
      helper: 'retour demandé',
    },
    {
      label: 'Activités récentes',
      value: systemStats?.recent_activity?.length || 0,
      helper: 'derniers événements visibles',
    },
  ];

  const statusChartData = useMemo(() => {
    return (systemStats?.plans_by_status || []).map((status) => ({
      label: status.statut,
      value: status.count,
      color: getStatusColor(status.statut),
    }));
  }, [systemStats]);

  const pipelineChartData = useMemo(() => {
    const inProgress = (statusCounts.brouillon || 0) + (statusCounts.analyse || 0) + (statusCounts['validé'] || 0);
    const approved = statusCounts['approuvé'] || 0;
    const revisions = (statusCounts['non validé'] || 0) + (statusCounts['non approuvé'] || 0);

    return [
      { label: 'En traitement', value: inProgress, color: '#3b82f6' },
      { label: 'Approuvés', value: approved, color: '#10b981' },
      { label: 'À corriger', value: revisions, color: '#ef4444' },
    ];
  }, [statusCounts]);

  if (loading) {
    return (
      <div className="admin-shell">
        <AdminSidebar />
        <div className="admin-content">
          <div className="admin-dashboard">
            <div className="loading-spinner">Chargement des statistiques...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <h1>Tableau de bord administrateur</h1>
        <div className="admin-actions">
          <button 
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Rafraîchissement...' : 'Rafraîchir'}
          </button>
          <button 
            className="btn-primary"
            onClick={() => navigate('/admin/roles')}
          >
            Gérer utilisateurs & rôles
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/admin/plans')}
          >
            Gérer les plans
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/admin/sessions')}
          >
            Gérer les sessions
          </button>
          <button 
            className="btn-secondary"
            onClick={() => navigate('/admin/activity')}
          >
            Journal d'activité
          </button>
        </div>
      </div>

      <div className="admin-kpi-strip">
        {dashboardKpis.map((kpi) => (
          <div key={kpi.label} className="admin-kpi-card">
            <span className="admin-kpi-label">{kpi.label}</span>
            <strong className="admin-kpi-value">{kpi.value}</strong>
            <span className="admin-kpi-helper">{kpi.helper}</span>
          </div>
        ))}
      </div>

      <div className="admin-graphs-grid">
        <VerticalBarChart
          title="Répartition des plans"
          subtitle="Volume de plans par statut"
          data={statusChartData}
        />
        <DonutChart
          title="Pipeline de validation"
          subtitle="Vue synthèse du flux de traitement"
          data={pipelineChartData}
        />
      </div>

      {/* Main Content Grid */}
      <div className="content-grid">
        {/* Utilisateurs par rôle */}
        <div className="dashboard-section">
          <h2>Utilisateurs par rôle</h2>
          <div className="role-stats">
            {systemStats?.users_by_role?.map((role) => (
              <div key={role.role_code} className="role-stat-item">
                <div 
                  className="role-badge"
                  style={{ backgroundColor: getRoleColor(role.role_code) }}
                >
                  {role.role_code}
                </div>
                <div className="role-stat-details">
                  <span className="role-name">{role.role_nom}</span>
                  <span className="role-count">{role.count} utilisateur(s)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Plans par statut */}
        <div className="dashboard-section">
          <h2>Plans par statut</h2>
          <div className="status-chart">
            {systemStats?.plans_by_status?.map((status) => (
              <div key={status.statut} className="status-bar">
                <div className="status-label">
                  <span>{status.statut}</span>
                  <span className="status-count">{status.count}</span>
                </div>
                <div className="status-progress">
                  <div 
                    className="status-fill"
                    style={{
                      width: `${(status.count / systemStats.total_plans * 100)}%`,
                      backgroundColor: getStatusColor(status.statut)
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Taux de validation */}
        <div className="dashboard-section">
          <h2>Taux de validation</h2>
          <div className="rate-stats">
            <div className="rate-item success">
              <div className="rate-circle">
                <span className="rate-value">{planStats?.taux_validation || 0}%</span>
              </div>
              <p>Taux de validation</p>
            </div>
            <div className="rate-item approval">
              <div className="rate-circle">
                <span className="rate-value">{planStats?.taux_approbation || 0}%</span>
              </div>
              <p>Taux d'approbation</p>
            </div>
            <div className="rate-item reject">
              <div className="rate-circle">
                <span className="rate-value">{planStats?.taux_rejet || 0}%</span>
              </div>
              <p>Taux de rejet</p>
            </div>
          </div>
        </div>

        {/* Validations par rôle */}
        <div className="dashboard-section">
          <h2>Validations par rôle</h2>
          <div className="validation-stats">
            {systemStats?.validations_by_role?.map((val) => (
              <div key={`${val.auteur_role || 'unknown'}-${val.count}`} className="validation-item">
                <span className="validation-role">{formatValidationRoleLabel(val.auteur_role)}</span>
                <span className="validation-count">{val.count} validation(s)</span>
              </div>
            ))}
          </div>
        </div>

        {/* Utilisateurs actifs */}
        <div className="dashboard-section wide">
          <h2>Utilisateurs les plus actifs (30 derniers jours)</h2>
          <div className="active-users-table">
            <table>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Validations</th>
                </tr>
              </thead>
              <tbody>
                {userStats?.active_users?.length > 0 ? (
                  userStats.active_users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.nom}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className="role-badge-small">
                          {user.role_nom}
                        </span>
                      </td>
                      <td>
                        <span className="validation-badge">
                          {user.validations_count}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">
                      Aucune activité récente
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Plans récents */}
        <div className="dashboard-section wide">
          <h2>Plans récemment soumis</h2>
          <div className="recent-plans-table">
            <table>
              <thead>
                <tr>
                  <th>Cours</th>
                  <th>Enseignant</th>
                  <th>Statut</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {systemStats?.recent_plans?.length > 0 ? (
                  systemStats.recent_plans.map((plan) => (
                    <tr key={plan.id}>
                      <td>{plan.cours}</td>
                      <td>{plan.enseignant_nom || 'N/A'}</td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getStatusColor(plan.statut) }}
                        >
                          {plan.statut}
                        </span>
                      </td>
                      <td>{new Date(plan.date_upload).toLocaleDateString('fr-FR')}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">
                      Aucun plan récent
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activité récente */}
        <div className="dashboard-section wide">
          <h2>Activité récente</h2>
          <div className="activity-timeline">
            {systemStats?.recent_activity?.length > 0 ? (
              systemStats.recent_activity.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="activity-content">
                    <p className="activity-text">
                      <strong>{activity.validateur_nom || 'Système'}</strong> a validé le plan 
                      <strong> {activity.cours}</strong>
                    </p>
                    <span className="activity-date">
                      {new Date(activity.date_validation).toLocaleString('fr-FR')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-data">Aucune activité récente</p>
            )}
          </div>
        </div>
      </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
